import { Component, type ErrorInfo, type ReactNode } from 'react';
import { Button } from './ui/button';

type Props = { children: ReactNode; onSignOut?: () => Promise<void> };
type State = { error: Error | null };

/**
 * Catches render failures so a bad page does not blank the whole SPA. Reload recovers the current hash;
 * Sign out clears the session when the shell still has a logout handler.
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error('Alder UI render error', error, info.componentStack);
  }

  private reload = () => { window.location.reload(); };

  private signOut = async () => {
    try {
      if (this.props.onSignOut) await this.props.onSignOut();
      else await fetch('/app-api/logout', { method: 'POST', credentials: 'same-origin' });
    } catch { /* still leave the broken screen */ }
    window.location.href = '/';
  };

  render() {
    if (!this.state.error) return this.props.children;
    return (
      <main id="main-content" className="queue-page shell-recover" role="alert">
        <header className="page-heading">
          <div>
            <span className="eyebrow">SOMETHING WENT WRONG</span>
            <h1>This page could not be shown<span className="heading-dot">.</span></h1>
            <p>Reload to try again, or sign out and come back later. Your data is safe.</p>
          </div>
        </header>
        <div className="shell-recover-actions">
          <Button onClick={this.reload}>Reload</Button>
          <Button variant="outline" onClick={() => void this.signOut()}>Sign out</Button>
        </div>
      </main>
    );
  }
}
